// @ts-check
import axiosInstance from '../axiosConfig';
import baseNormalizeError from '../utils/normalizeError';

const GUEST_SESSION_KEY = 'nls_cart_session_token'; // Stores HMAC-signed session token

/**
 * @typedef {{ retail?: number|null, educator?: number|null, school?: number|null, currency?: string }} Price
 * @typedef {{ _id: string, productId: string, title: string, image?: string|null, quantity: number, price?: Price|null, slug?: string, type?: 'product'|'bundle' }} CartItem
 * @typedef {{ _id?: string, sessionId?: string, currency?: string, items: CartItem[], subtotal?: number, discountTotal?: number, shippingEstimate?: number, total?: number, coupon?: { code: string, discountType: string, discountValue: number }|null, updatedAt?: string }} Cart
 */

const getSessionToken = () => localStorage.getItem(GUEST_SESSION_KEY);

const saveSessionToken = (/** @type {any} */ raw) => {
  // Only save when the server returns a new signed token (new guest session)
  if (raw?.sessionToken) {
    localStorage.setItem(GUEST_SESSION_KEY, raw.sessionToken);
  }
};

/**
 * @param {Record<string, any>} [config]
 */
const withGuestSession = (config = {}) => {
  const token = getSessionToken();
  if (!token) return config;
  return {
    ...config,
    headers: { ...(config.headers || {}), 'x-session-id': token },
  };
};

/**
 * @param {any} raw
 * @returns {Cart}
 */
const normalizeCart = (raw) => {
  const items = Array.isArray(raw?.items) ? raw.items : [];

  return {
    _id: raw?._id,
    sessionId: raw?.sessionId,
    currency: raw?.currency || 'USD',
    subtotal: Number(raw?.subtotal || 0),
    discountTotal: Number(raw?.discountTotal || 0),
    shippingEstimate: Number(raw?.shippingEstimate || 0),
    total: Number(raw?.total || 0),
    updatedAt: raw?.updatedAt,
    coupon: raw?.coupon
      ? {
          code: raw.coupon.code,
          discountType: raw.coupon.discountType || raw.coupon.type || 'fixed',
          discountValue: Number(raw.coupon.discountValue ?? raw.coupon.value ?? 0),
        }
      : null,
    items: items.map((/** @type {any} */ item) => {
      const entity = item.product || item.bundle || item;
      const entityId = entity?._id || item.productId || item.bundleId || '';
      const title = entity?.title || entity?.name || item.title || '';
      const image = entity?.coverImage || entity?.image || item.image || null;
      const slug = entity?.slug || item.slug || null;
      const currency = raw?.currency || 'USD';
      const unitPrice =
        typeof item.unitPrice === 'number'
          ? item.unitPrice
          : (typeof item.price?.retail === 'number' ? item.price.retail : null);

      // Extract per-segment prices from the populated product if available,
      // otherwise fall back to the stored unitPrice for all segments.
      const productPrices = item.product?.prices;
      const bundlePrices = item.bundle?.prices;
      let price = null;

      if (productPrices?.length) {
        const pd = productPrices.find((/** @type {any} */ p) => p.currency === currency)
          || productPrices.find((/** @type {any} */ p) => p.currency === 'USD')
          || productPrices[0];
        if (pd) {
          price = {
            retail: pd.retailPrice ?? unitPrice,
            educator: typeof pd.educatorPrice === 'number' ? pd.educatorPrice : (pd.retailPrice ?? unitPrice),
            school: typeof pd.schoolPrice === 'number' ? pd.schoolPrice : (pd.retailPrice ?? unitPrice),
            currency,
          };
        }
      } else if (bundlePrices?.length) {
        const pd = bundlePrices.find((/** @type {any} */ p) => p.currency === currency)
          || bundlePrices.find((/** @type {any} */ p) => p.currency === 'USD')
          || bundlePrices[0];
        const bp = pd?.bundlePrice ?? unitPrice;
        price = bp === null ? null : { retail: bp, educator: bp, school: bp, currency };
      } else if (unitPrice !== null) {
        price = { retail: unitPrice, educator: unitPrice, school: unitPrice, currency };
      }

      return {
        _id: item._id,
        productId: String(entityId),
        title,
        image,
        quantity: Number(item.quantity || 1),
        price,
        slug,
        type: item.bundle ? 'bundle' : 'product',
      };
    }),
  };
};

/** @param {unknown} err @returns {{ status: number, message: string, field: string|null }} */
const normalizeError = (err) => {
  const result = baseNormalizeError(err);

  // If the cart was not found (expired guest session), clear the stale
  // session ID so the next operation creates a fresh cart.
  if (result.status === 404 && result.message.toLowerCase().includes('cart not found')) {
    localStorage.removeItem(GUEST_SESSION_KEY);
  }

  return result;
};

/** @returns {Promise<Cart>} */
export const getCart = async () => {
  try {
    const { data } = await axiosInstance.get('/cart', withGuestSession());
    const raw = data.data ?? data;
    saveSessionToken(raw);
    return normalizeCart(raw);
  } catch (err) {
    throw normalizeError(err);
  }
};

/**
 * @param {string} itemId
 * @param {number} quantity
 * @param {{ type?: 'product'|'bundle' }} [options]
 * @returns {Promise<Cart>}
 */
export const addToCart = async (itemId, quantity = 1, options = {}) => {
  try {
    const type = options.type === 'bundle' ? 'bundleId' : 'productId';
    const { data } = await axiosInstance.post('/cart/items', { [type]: itemId, quantity }, withGuestSession());
    const raw = data.data ?? data;
    saveSessionToken(raw);
    return normalizeCart(raw);
  } catch (err) {
    throw normalizeError(err);
  }
};

/**
 * @param {string} itemId
 * @param {number} quantity
 * @returns {Promise<Cart>}
 */
export const updateCartItem = async (itemId, quantity) => {
  try {
    // Backend route is PUT /cart/items/:itemId (not PATCH)
    const { data } = await axiosInstance.put(`/cart/items/${itemId}`, { quantity }, withGuestSession());
    const raw = data.data ?? data;
    saveSessionToken(raw);
    return normalizeCart(raw);
  } catch (err) {
    throw normalizeError(err);
  }
};

/**
 * @param {string} itemId
 * @returns {Promise<Cart>}
 */
export const removeFromCart = async (itemId) => {
  try {
    const { data } = await axiosInstance.delete(`/cart/items/${itemId}`, withGuestSession());
    const raw = data.data ?? data;
    saveSessionToken(raw);
    return normalizeCart(raw);
  } catch (err) {
    throw normalizeError(err);
  }
};

/**
 * @param {string} code
 * @returns {Promise<Cart>}
 */
export const applyCartCoupon = async (code) => {
  try {
    const { data } = await axiosInstance.post('/cart/coupon', { code }, withGuestSession());
    const raw = data.data ?? data;
    saveSessionToken(raw);
    return normalizeCart(raw);
  } catch (err) {
    throw normalizeError(err);
  }
};

/** @returns {Promise<Cart>} */
export const removeCartCoupon = async () => {
  try {
    const { data } = await axiosInstance.delete('/cart/coupon', withGuestSession());
    const raw = data.data ?? data;
    saveSessionToken(raw);
    return normalizeCart(raw);
  } catch (err) {
    throw normalizeError(err);
  }
};

/**
 * Merge a guest cart into the authenticated user's cart.
 * Call after login/register when a guest session exists.
 * @returns {Promise<Cart|null>}
 */
export const mergeGuestCart = async () => {
  const token = getSessionToken();
  if (!token) return null;
  try {
    const { data } = await axiosInstance.post('/cart/merge', { sessionId: token });
    const cart = normalizeCart(data.data ?? data);
    localStorage.removeItem(GUEST_SESSION_KEY);
    return cart;
  } catch {
    return null;
  }
};
